import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { auth } from "../components/firebase";

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const isPasswordProviderUser = (currentUser) =>
  Boolean(currentUser?.providerData?.some((p) => p?.providerId === "password"));

const isEmailVerificationPending = (currentUser) =>
  Boolean(currentUser) && isPasswordProviderUser(currentUser) && !currentUser.emailVerified;

const createEmailActionSettings = () => {
  if (typeof window === "undefined") return undefined;
  const handlerUrl = new URL("/auth/action", window.location.origin);
  handlerUrl.searchParams.set("next", "/");

  return {
    url: handlerUrl.toString(),
    handleCodeInApp: false,
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const err = url.searchParams.get("error");
      const sub = url.searchParams.get("error_subtype");
      if (err) console.error("Google redirect URL error:", err, sub, url.toString());
    } catch {}

  getRedirectResult(auth)
    .then((res) => {
        if (res?.user) console.log("Redirect OK:", res.user.email);
      })
      .catch((e) => {
        console.error("Redirect error:", e?.code, e?.message, e?.customData?.email, e);
      });
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (isEmailVerificationPending(currentUser)) {
        await signOut(auth);
        setUser(null);
        setLoading(false);
        return;
      }

      setUser(currentUser || null);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const signup = async (email, password, profile = {}) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    const firstName = String(profile?.firstName || "").trim();
    const lastName = String(profile?.lastName || "").trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName) {
      await updateProfile(credential.user, { displayName: fullName });
    }

    await sendEmailVerification(credential.user, createEmailActionSettings());
    await signOut(auth);

    return {
      emailVerificationSent: true,
      email: credential?.user?.email || email,
      displayName: fullName,
    };
  };

  const signin = async (email, password) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    await credential.user.reload();

    if (isEmailVerificationPending(credential.user)) {
      await signOut(auth);
      const error = new Error("Email not verified");
      error.code = "auth/email-not-verified";
      throw error;
    }

    return credential;
  };

  const logout = () => signOut(auth);
  const resetPassword = (email) =>
    sendPasswordResetEmail(auth, email, createEmailActionSettings());

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      return await signInWithPopup(auth, provider);
    } catch (error) {
      const code = error?.code || "";

      if ((code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") && auth.currentUser) {
        return { user: auth.currentUser };
      }

      if (code === "auth/popup-blocked") {
        await signInWithRedirect(auth, provider);
        return null;
      }

      throw error;
    }
  };

  const value = useMemo( () => ({ user, loading, signup, signin, logout, signInWithGoogle, resetPassword }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
